import { History } from 'history';
import _ from 'lodash';
import queryString from 'query-string';
import React, { Dispatch, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { AnyAction } from 'redux';
import { AppRoute } from 'const';
import { getProjectData } from 'containers/WelcomeScreen/actions';
import { isInIframe } from 'utils/isInIframe';
import { isUnsupportedBrowser } from 'utils/isUnsupportedBrowser';
import { setStorageItems } from 'utils/setStorageItems';

const getParentOrigin = (): string => {
  if (!document.referrer) {
    return window.location.origin;
  }

  try {
    return new URL(document.referrer).origin;
  } catch (error) {

    // eslint-disable-next-line no-console
    console.warn(error);

    return window.location.origin;
  }
};

async function getSessionId(query: queryString.ParsedQuery): Promise<string | undefined> {
  if (query.sessionId) {
    return query.sessionId as string;
  }

  return new Promise((resolve) => {
    const MAX_MESSAGES = 3;
    let messageCount = 0;

    function parseEvent({ data }: MessageEvent): void {
      if (!_.isString(data)) {
        return;
      }

      try {
        const { message_id: messageId, data: messageData } = JSON.parse(data);

        if (messageId === 'session_id') {
          resolve(messageData.session_id);
          window.removeEventListener('message', parseEvent);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('Error occurred during parsing a message: ', error);
      } finally {
        if (messageCount++ > MAX_MESSAGES) {
          window.removeEventListener('message', parseEvent);
        }
      }
    }
    // https://developer.veevavault.com/docs/#sending-session-ids-with-post-message
    window.addEventListener('message', parseEvent);
    window.parent.postMessage(
      JSON.stringify({ message_id: 'ready', data: {} }),
      getParentOrigin(),
    );
  });
}

function handleIframe(
  history: History,
  projectPath: string,
  storageItems: Record<string, string>,
): void {
  history.replace(AppRoute.IFRAME_PLACEHOLDER);

  const assemblerWindow = window.open(projectPath, '_blank');
  // some browsers don't inherit session storage items from iframe for some reason
  if (assemblerWindow) {
    setStorageItems(assemblerWindow.sessionStorage, storageItems);
  }
}

async function openProject(
  search: string,
  dispatch: Dispatch<AnyAction>,
  history: History,
): Promise<void> {
  if (isUnsupportedBrowser()) {
    history.replace(AppRoute.UNSUPPORTED_BROWSER);

    return;
  }
  const query = queryString.parse(search);
  const { userId, documentId, source = '' } = query;
  const sessionId = await getSessionId(query);
  if (sessionId && userId && documentId) {
    const projectPath = `/${AppRoute.PROJECT}/${documentId}`;
    const storageItems = {
      'pm-sessionId': sessionId,
      'pm-userId': userId as string,
      'veeva-domain': source as string,
    };

    setStorageItems(sessionStorage, storageItems);
    if (isInIframe()) {
      handleIframe(history, projectPath, storageItems);
    } else {
      dispatch(getProjectData(Number(documentId)));
      history.replace(projectPath);
    }

    return;
  }
  history.replace(AppRoute.NO_MATCH);
}

const WelcomeScreen: React.FunctionComponent<RouteComponentProps> = (props) => {
  const dispatch = useDispatch();
  const { history, location: { search } } = props;

  useEffect(
    () => {
      void openProject(search, dispatch, history);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return null;
};

export default WelcomeScreen;
